import {TagSchemaItemType} from 'graphql-client/__generated/graphqlGlobalTypes';

import {BenchlingIconName} from './BenchlingIcon';

/**
 * Enum values are expected to be valid icon names acceptable by BenchlingIcon, or otherwise you may introduce
 * a mapping in IconAliases to map to a valid icon name.
 */
export enum FolderItemIcon {
  BasicFolderItem = 'basic_folder_item',
  Sequence = 'sequence',
  Plasmid = 'plasmid',
  RNASequence = 'rna_sequence',
  OligoV2 = 'oligo-v2',
  Oligo = 'oligo',
  OligoConjugate = 'network-wired',
  PrimerPair = 'primer_pair',
  Protein = 'protein',
  Entry = 'entry',
  Template = 'template',
  Mixture = 'mixture',
  Molecule = 'molecule',
  Antibody = 'antibody',
  Bacteria = 'bacteria',
  CellLine = 'cell-line',
  Chromatography = 'chromatography',
  Compound = 'compound',
  Equipment = 'microscope',
  Human = 'human',
  Linear = 'linear',
  Material = 'material',
  MortarPestle = 'mortar-pestle',
  Mouse = 'mouse',
  Plant = 'plant',
  Powder = 'powder',
  PowderBeaker = 'powder-beaker',
  Recipe = 'recipe',
  SampleSlide = 'sample-slide',
  SmallMolecule = 'small-molecule',
  SmallMoleculeBeaker = 'small-molecule-beaker',
  Solution = 'solution',
  Worm = 'worm',
  Worksheet = 'worksheet',
  Flask = 'flask',
  Reaction = 'reaction-vessel',
}

/**
 * Keep in sync with the backend *SchemaIconId DGEnums
 * (defined in benchling/benchling_domain_graph/sdl/schemas/ under *.graphql files)
 * and the _NON_ENTITY_ICON_NAMES dict in scripts/schemas/audit_icon_ids.py.
 */
export const FOLDER_ITEM_ICONS_BY_ITEM_TYPE: Record<TagSchemaItemType, Array<BenchlingIconName>> = {
  [TagSchemaItemType.AA_SEQUENCE]: [
    FolderItemIcon.Protein,
    FolderItemIcon.Linear,
    FolderItemIcon.BasicFolderItem,
  ],
  [TagSchemaItemType.ANTIBODY]: [FolderItemIcon.Antibody],
  [TagSchemaItemType.BASIC_FOLDER_ITEM]: [
    FolderItemIcon.BasicFolderItem,
    FolderItemIcon.Sequence,
    FolderItemIcon.RNASequence,
    FolderItemIcon.OligoV2,
    FolderItemIcon.PrimerPair,
    FolderItemIcon.Protein,
    FolderItemIcon.Antibody,
    FolderItemIcon.Bacteria,
    FolderItemIcon.CellLine,
    FolderItemIcon.Chromatography,
    FolderItemIcon.Compound,
    FolderItemIcon.Human,
    FolderItemIcon.Linear,
    FolderItemIcon.Material,
    FolderItemIcon.Mouse,
    FolderItemIcon.Plant,
    FolderItemIcon.SampleSlide,
    FolderItemIcon.SmallMolecule,
    FolderItemIcon.SmallMoleculeBeaker,
    FolderItemIcon.Worm,
  ],
  [TagSchemaItemType.DNA_SEQUENCE]: [
    FolderItemIcon.Sequence,
    FolderItemIcon.Plasmid,
    FolderItemIcon.OligoV2,
    FolderItemIcon.PrimerPair,
    FolderItemIcon.Linear,
    FolderItemIcon.BasicFolderItem,
    FolderItemIcon.RNASequence,
  ],
  [TagSchemaItemType.ENTRY]: [FolderItemIcon.Entry],
  [TagSchemaItemType.EQUIPMENT]: [FolderItemIcon.Equipment],
  [TagSchemaItemType.MIXED]: [
    FolderItemIcon.Sequence,
    FolderItemIcon.Plasmid,
    FolderItemIcon.OligoV2,
    FolderItemIcon.PrimerPair,
    FolderItemIcon.Linear,
    FolderItemIcon.BasicFolderItem,
    FolderItemIcon.RNASequence,
  ],
  [TagSchemaItemType.MIXTURE]: [
    FolderItemIcon.Mixture,
    FolderItemIcon.MortarPestle,
    FolderItemIcon.Powder,
    FolderItemIcon.PowderBeaker,
    FolderItemIcon.Recipe,
    FolderItemIcon.Solution,
  ],
  [TagSchemaItemType.MOLECULE]: [FolderItemIcon.SmallMolecule, FolderItemIcon.SmallMoleculeBeaker],
  [TagSchemaItemType.DNA_OLIGO]: [FolderItemIcon.OligoV2, FolderItemIcon.Oligo, FolderItemIcon.Linear],
  [TagSchemaItemType.OLIGO_CONJUGATE]: [FolderItemIcon.OligoConjugate],
  [TagSchemaItemType.OLIGO_DUPLEX]: [FolderItemIcon.OligoConjugate],
  [TagSchemaItemType.RNA_OLIGO]: [FolderItemIcon.OligoV2, FolderItemIcon.Oligo, FolderItemIcon.Linear],
  [TagSchemaItemType.RNA_SEQUENCE]: [
    FolderItemIcon.RNASequence,
    FolderItemIcon.Sequence,
    FolderItemIcon.OligoV2,
    FolderItemIcon.PrimerPair,
    FolderItemIcon.Linear,
    FolderItemIcon.BasicFolderItem,
  ],
  [TagSchemaItemType.TRIAL_SUBJECT]: [FolderItemIcon.BasicFolderItem],
};
